/**
 * @module AdapterDetailPage
 * @package @geenius/adapters/solidjs
 * @description Renders the single-domain adapter detail view for the SolidJS
 * variant. The page surfaces current provider health, available providers, and
 * inline configuration for one adapter domain.
 */

import type { AdapterDomain, DomainAdapterConfig } from "@geenius/adapters";
import {
  DOMAIN_DESCRIPTIONS,
  DOMAIN_ICONS,
  DOMAIN_LABELS,
  getProvidersForDomain,
} from "@geenius/adapters";
import type { Accessor, JSX } from "solid-js";
import {
  createMemo,
  createSignal,
  createUniqueId,
  ErrorBoundary,
  For,
  Show,
} from "solid-js";
import { AdapterConfigForm } from "../components/AdapterConfigForm";
import { AdapterStatusBadge } from "../components/AdapterStatusBadge";
import { cn } from "../lib/utils";
import { createAdapterStatuses, createAdapters } from "../primitives";
import { PageBoundaryFallback } from "./PageBoundaryFallback";

/**
 * Props for rendering the SolidJS single-domain adapter detail page.
 *
 * @property domain Adapter domain whose provider details should be displayed.
 * @property onBack Optional callback for returning to the dashboard view.
 * @property onConfigure Optional callback invoked with submitted adapter configuration.
 * @property class Optional class name merged into the page container.
 */
export interface AdapterDetailPageProps {
  domain: AdapterDomain;
  onBack?: () => void;
  onConfigure?: (
    domain: AdapterDomain,
    config: DomainAdapterConfig,
  ) => Promise<void> | void;
  class?: string;
}

/**
 * Renders the SolidJS adapter detail page for one adapter domain.
 *
 * @param props Page props including the selected domain and optional back-navigation handler.
 * @returns The detail view with provider health, available providers, and configuration affordances.
 */
export function AdapterDetailPage(props: AdapterDetailPageProps): JSX.Element {
  return (
    <ErrorBoundary
      fallback={(error, reset) => (
        <PageBoundaryFallback
          class={props.class}
          error={error}
          onRetry={reset}
          title={`${DOMAIN_LABELS[props.domain]} adapter failed to load`}
        />
      )}
    >
      <AdapterDetailPageInner {...props} />
    </ErrorBoundary>
  );
}

function AdapterDetailPageInner(props: AdapterDetailPageProps): JSX.Element {
  const adapters = createAdapters();
  const statuses = createAdapterStatuses();
  const status = createMemo(() => statuses[props.domain]);
  const providers = createMemo(() => getProvidersForDomain(props.domain));
  const [isEditing, setIsEditing] = createSignal(false);
  const latency = () => status().latency ?? 0;
  // Mirror the React reference's selectable filter so the detail page surfaces
  // which DB providers are actually wirable today vs. deferred/BYOD. The config
  // form hides them; the detail page shows them with an "Unavailable" tag so
  // consumers learn the launch axis without losing discovery.
  const launchSelectableIds = createMemo(
    () =>
      new Set(
        props.domain === "db"
          ? ["localStorage", "convex", "neon", "cloudflareKV", "memory"]
          : providers().map((p) => p.id),
      ),
  );
  // Stable ids for label/meter pairing so screen readers announce the latency
  // meter with its label rather than reading the visual bar in isolation.
  const latencyLabelId = createUniqueId();

  // Loading skeleton: matches the React reference and gives assistive tech a
  // role="status" announcement instead of flashing the page through provider
  // boot. motion-reduce disables the pulse for users who opt out.
  return (
    <Show
      when={!adapters.isLoading}
      fallback={
        <div class={cn("mx-auto max-w-3xl space-y-6", props.class)}>
          <div
            class="animate-pulse motion-reduce:animate-none space-y-4"
            role="status"
            aria-label={`Loading ${DOMAIN_LABELS[props.domain]} adapter`}
          >
            <div class="h-6 w-32 rounded bg-muted" />
            <div class="flex items-start gap-5">
              <div class="h-16 w-16 rounded-lg bg-muted" />
              <div class="flex-1 space-y-2">
                <div class="h-7 w-48 rounded bg-muted" />
                <div class="h-4 w-72 rounded bg-muted/70" />
              </div>
            </div>
            <div class="h-32 rounded-lg bg-muted/60" />
            <div class="h-48 rounded-lg bg-muted/60" />
          </div>
        </div>
      }
    >
      <DetailPageContent
        adapters={adapters}
        domain={props.domain}
        status={status}
        providers={providers}
        launchSelectableIds={launchSelectableIds}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        latency={latency}
        latencyLabelId={latencyLabelId}
        onBack={props.onBack}
        onConfigure={props.onConfigure}
        class={props.class}
      />
    </Show>
  );
}

interface DetailContentProps {
  adapters: ReturnType<typeof createAdapters>;
  domain: AdapterDomain;
  status: Accessor<ReturnType<typeof createAdapterStatuses>[AdapterDomain]>;
  providers: Accessor<ReturnType<typeof getProvidersForDomain>>;
  launchSelectableIds: Accessor<Set<string>>;
  isEditing: () => boolean;
  setIsEditing: (next: boolean) => void;
  latency: () => number;
  latencyLabelId: string;
  onBack?: () => void;
  onConfigure?: (
    domain: AdapterDomain,
    config: DomainAdapterConfig,
  ) => Promise<void> | void;
  class?: string;
}

function DetailPageContent(props: DetailContentProps): JSX.Element {
  return (
    <div class={cn("mx-auto max-w-3xl space-y-8 text-foreground", props.class)}>
      <Show when={props.onBack}>
        <button
          type="button"
          onClick={props.onBack}
          class="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none"
        >
          ← Back to Adapters
        </button>
      </Show>

      <div class="flex items-start gap-5">
        <div class="flex h-16 w-16 items-center justify-center rounded-lg border border-border bg-card text-3xl shadow-sm">
          {DOMAIN_ICONS[props.domain]}
        </div>
        <div class="flex-1">
          <div class="flex items-center gap-3">
            <h1 class="text-2xl font-extrabold text-foreground">
              {DOMAIN_LABELS[props.domain]}
            </h1>
            <AdapterStatusBadge status={props.status().status} />
          </div>
          <p class="mt-1 text-sm text-muted-foreground">
            {DOMAIN_DESCRIPTIONS[props.domain]}
          </p>
        </div>
      </div>

      <div class="rounded-lg border border-border bg-card p-6 shadow-sm">
        <h2 class="mb-4 text-xs font-semibold uppercase text-muted-foreground">
          Current Status
        </h2>
        <div class="grid gap-4 sm:grid-cols-3">
          <div>
            <p class="text-[0.625rem] uppercase text-muted-foreground">
              Provider
            </p>
            <p class="mt-0.5 text-sm font-semibold text-foreground">
              {props.status().provider !== "none"
                ? props.status().provider
                : "None"}
            </p>
          </div>
          <div>
            <p class="text-[0.625rem] uppercase text-muted-foreground">
              Status
            </p>
            <p class="mt-0.5">
              <AdapterStatusBadge status={props.status().status} />
            </p>
          </div>
          <div>
            <p class="text-[0.625rem] uppercase text-muted-foreground">
              Last Checked
            </p>
            <p class="mt-0.5 text-sm text-muted-foreground">
              <Show
                when={props.status().lastCheckedAt}
                fallback={"Not checked"}
              >
                {(lastCheckedAt) =>
                  new Date(lastCheckedAt()).toLocaleTimeString()
                }
              </Show>
            </p>
          </div>
        </div>

        <Show when={props.status().error}>
          {/* role="alert" matches React reference so screen readers announce
              the error when status flips, instead of treating it as inert text. */}
          <div
            class="mt-4 rounded-md border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive"
            role="alert"
          >
            <p>
              <span class="font-semibold">Error:</span> {props.status().error}
            </p>
            <button
              type="button"
              onClick={() => props.setIsEditing(true)}
              class="mt-3 rounded-md border border-destructive/30 bg-background px-3 py-1.5 text-xs font-semibold text-destructive transition-colors hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none"
            >
              Review configuration
            </button>
          </div>
        </Show>

        <Show when={props.status().latency}>
          <div class="mt-4">
            <p
              id={props.latencyLabelId}
              class="mb-1 text-[0.625rem] uppercase text-muted-foreground"
            >
              Latency
            </p>
            {/* Semantic <meter>: assistive tech reads the meter as a labelled
                gauge with min/max/value rather than as decorative geometry.
                The visual styled bar below is aria-hidden because the meter
                already exposes the value to AT. */}
            <meter
              aria-labelledby={props.latencyLabelId}
              aria-valuetext={`${props.latency()} milliseconds`}
              class="sr-only"
              min="0"
              max="1000"
              low="100"
              high="500"
              value={props.latency()}
            >
              {props.latency()}ms
            </meter>
            <div
              aria-hidden="true"
              class="h-2 w-full overflow-hidden rounded-full bg-muted"
            >
              <div
                class={cn(
                  "h-full rounded-full transition-all motion-reduce:transition-none",
                  props.latency() < 100
                    ? "bg-primary"
                    : props.latency() < 500
                      ? "bg-accent"
                      : "bg-destructive",
                )}
                style={{
                  width: `${Math.min(100, (props.latency() / 1000) * 100)}%`,
                }}
              />
            </div>
            <p class="mt-1 text-xs text-muted-foreground">
              {props.latency()}ms
            </p>
          </div>
        </Show>
      </div>

      <div class="rounded-lg border border-border bg-card p-6 shadow-sm">
        <div class="mb-4 flex items-center justify-between">
          <h2 class="text-xs font-semibold uppercase text-muted-foreground">
            Available Providers
          </h2>
          <span class="text-xs text-muted-foreground">
            {props.providers().length} providers
          </span>
        </div>
        <div class="divide-y divide-border">
          <For each={props.providers()}>
            {(provider) => {
              const isAvailable = () =>
                props.launchSelectableIds().has(provider.id);
              return (
                <div
                  class="flex items-center justify-between py-3 first:pt-0 last:pb-0"
                  data-available={isAvailable() ? "true" : "false"}
                >
                  <div>
                    <p class="text-sm font-semibold text-foreground">
                      {provider.name}
                    </p>
                    <p class="text-xs text-muted-foreground">
                      {provider.description}
                    </p>
                  </div>
                  <div class="flex flex-col items-end gap-1">
                    <span
                      class={cn(
                        "self-start rounded px-2 py-0.5 text-[0.625rem] font-bold uppercase",
                        provider.tier === "pronto"
                          ? "bg-primary/10 text-primary"
                          : provider.tier === "lancio"
                            ? "bg-accent/10 text-accent"
                            : "bg-muted text-muted-foreground",
                      )}
                    >
                      {provider.tier}
                    </span>
                    <Show when={!isAvailable()}>
                      <span class="self-start rounded border border-border bg-muted px-2 py-0.5 text-[0.625rem] font-semibold uppercase text-muted-foreground">
                        Unavailable
                      </span>
                    </Show>
                  </div>
                </div>
              );
            }}
          </For>
        </div>
      </div>

      <Show
        when={props.isEditing()}
        fallback={
          <button
            type="button"
            onClick={() => props.setIsEditing(true)}
            class="w-full rounded-lg border border-dashed border-border bg-card/40 p-6 text-center text-sm text-muted-foreground transition-colors hover:border-primary/30 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none"
          >
            Configure {DOMAIN_LABELS[props.domain]} Adapter
          </button>
        }
      >
        <AdapterConfigForm
          domain={props.domain}
          onSave={async (config) => {
            // Forward the submitted config to the consumer before closing the
            // form. The React reference relies on this to persist domain
            // configuration; prior SolidJS implementation dropped the payload.
            await props.onConfigure?.(props.domain, config);
            props.setIsEditing(false);
          }}
          onCancel={() => props.setIsEditing(false)}
        />
      </Show>
    </div>
  );
}
